import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { Download } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { showToast } from '@/shared/ui/toast';

import type { ClinicalRecordPrintPatient } from './clinical-record-print';
import {
  CLINICAL_IMAGING_STUDIES_QUERY,
  INTERPRET_IMAGING_STUDY_MUTATION,
  REGISTER_IMAGING_STUDY_MUTATION,
  idempotencyContext,
} from './clinical.gql';
import { printImagingOrder } from './imaging-order-print';

/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call */

const STUDY_KINDS = ['xray', 'ultrasound', 'ct_scan', 'mri', 'other'] as const;

function errText(e: unknown): string {
  if (e instanceof ApolloError) return e.graphQLErrors[0]?.message ?? e.message;
  if (e instanceof Error) return e.message;
  return 'error';
}

function Area(props: { value: string; placeholder: string; onChange: (v: string) => void }): JSX.Element {
  return (
    <textarea
      className="border-input bg-background min-h-[48px] w-full rounded-md border px-2 py-1 text-sm"
      rows={2}
      value={props.value}
      placeholder={props.placeholder}
      onChange={(e) => props.onChange(e.target.value)}
    />
  );
}

function StudyCard({
  study,
  canWrite,
  appendable,
  onError,
  onChanged,
}: {
  study: any;
  canWrite: boolean;
  appendable: boolean;
  onError: (m: string) => void;
  onChanged: () => Promise<unknown>;
}): JSX.Element {
  const { t } = useTranslation();
  const [interpret] = useMutation(INTERPRET_IMAGING_STUDY_MUTATION);
  const [findings, setFindings] = useState<string>(study.findings ?? '');
  const [interpretation, setInterpretation] = useState<string>(study.interpretation ?? '');

  const onSave = async (): Promise<void> => {
    try {
      await interpret({
        variables: {
          input: {
            imaging_study_id: study.id,
            findings: findings.trim() || undefined,
            interpretation: interpretation.trim() || undefined,
          },
        },
        context: idempotencyContext(),
      });
      await onChanged();
      showToast({ tone: 'success', title: t('clinical.toast_imaging_interpreted') });
    } catch (e) {
      onError(errText(e));
    }
  };

  return (
    <div className="space-y-2 rounded-md border p-2">
      <p className="flex items-center gap-2 text-sm font-medium">
        <Badge variant="outline">{t(`clinical.study_kind_${study.study_kind}`)}</Badge>
        {study.body_region ? <span className="text-muted-foreground">{study.body_region}</span> : null}
      </p>
      {canWrite && appendable ? (
        <>
          <div className="space-y-1">
            <Label>{t('clinical.imaging_findings')}</Label>
            <Area value={findings} placeholder={t('clinical.imaging_findings')} onChange={setFindings} />
          </div>
          <div className="space-y-1">
            <Label>{t('clinical.imaging_interpretation')}</Label>
            <Area value={interpretation} placeholder={t('clinical.imaging_interpretation')} onChange={setInterpretation} />
          </div>
          <Button size="sm" onClick={() => void onSave()}>
            {t('clinical.save_section')}
          </Button>
        </>
      ) : (
        <div className="text-sm">
          {study.findings ? <p>{study.findings}</p> : null}
          {study.interpretation ? (
            <p className="text-muted-foreground">{study.interpretation}</p>
          ) : null}
        </div>
      )}
    </div>
  );
}

export function ImagingSection({
  visitId,
  canWrite,
  editable,
  appendable,
  printBase,
  chiefComplaint,
  primaryDiagnosis,
}: {
  visitId: string;
  canWrite: boolean;
  editable: boolean;
  appendable: boolean;
  printBase: ClinicalRecordPrintPatient;
  chiefComplaint?: string | null;
  primaryDiagnosis?: string | null;
}): JSX.Element {
  const { t } = useTranslation();
  const { data, refetch } = useQuery(CLINICAL_IMAGING_STUDIES_QUERY, {
    variables: { visit_id: visitId },
    fetchPolicy: 'cache-and-network',
  });
  const studies: any[] = data?.clinicalImagingStudies ?? [];
  const [register] = useMutation(REGISTER_IMAGING_STUDY_MUTATION);

  const [form, setForm] = useState({ study_kind: 'xray', body_region: '', findings: '' });
  const [error, setError] = useState<string | null>(null);

  const onDownloadOrder = async (): Promise<void> => {
    const ok = await printImagingOrder(
      {
        patient: printBase,
        chief_complaint: chiefComplaint,
        primary_diagnosis: primaryDiagnosis,
        studies,
      },
      t,
    );
    if (!ok) showToast({ tone: 'error', title: t('clinical.record_print_blocked') });
  };

  const onRegister = async (): Promise<void> => {
    setError(null);
    try {
      await register({
        variables: {
          input: {
            clinical_visit_id: visitId,
            study_kind: form.study_kind,
            body_region: form.body_region.trim() || undefined,
            findings: form.findings.trim() || undefined,
          },
        },
        context: idempotencyContext(),
      });
      setForm({ study_kind: 'xray', body_region: '', findings: '' });
      await refetch();
      showToast({ tone: 'success', title: t('clinical.toast_imaging_registered') });
    } catch (e) {
      setError(errText(e));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" variant="outline" onClick={() => void onDownloadOrder()}>
          <Download className="h-4 w-4" aria-hidden />
          <span>{t('clinical.download_imaging_order')}</span>
        </Button>
      </div>

      {error ? (
        <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-2 text-sm">
          {error}
        </div>
      ) : null}

      {studies.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('clinical.no_imaging')}</p>
      ) : (
        <div className="space-y-2">
          {studies.map((study) => (
            <StudyCard
              key={study.id}
              study={study}
              canWrite={canWrite}
              appendable={appendable}
              onError={setError}
              onChanged={refetch}
            />
          ))}
        </div>
      )}

      {canWrite && editable ? (
        <div className="space-y-2 rounded-md border p-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label>{t('clinical.study_kind')}</Label>
              <select
                className="border-input bg-background h-10 w-full rounded-md border px-2 text-sm"
                value={form.study_kind}
                onChange={(e) => setForm({ ...form, study_kind: e.target.value })}
              >
                {STUDY_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t(`clinical.study_kind_${k}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label>{t('clinical.imaging_body_region')}</Label>
              <Input value={form.body_region} onChange={(e) => setForm({ ...form, body_region: e.target.value })} />
            </div>
          </div>
          <Button size="sm" onClick={() => void onRegister()}>
            {t('clinical.register_study')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
